import { DmpConnectorSettings, SecurityLabelEnum } from '@domain/types';
import { Tooltip } from '@honestica/design-system';
import {
  BannerCard,
  colors,
  Form,
  Icon,
  Radio,
  RadioChangeEvent,
  Space,
  Switch,
  Typography,
} from '@honestica/ui-kit/src';
import { tracking } from '@services/analytics';
import { Dispatch, SetStateAction } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styles from './SecretConnectionBlock.module.less';

type SecretConnectionBlockProps = {
  originalSettings?: DmpConnectorSettings | undefined;
  settings: DmpConnectorSettings;
  setSettings: Dispatch<SetStateAction<DmpConnectorSettings>>;
  isReadOnly: boolean;
};

const DISPLAY = 'display';
const HIDDEN = 'hidden';

const computeParentsVisibility = (securityLabels: SecurityLabelEnum[]): 'display' | 'hidden' => {
  if (securityLabels.includes(SecurityLabelEnum.HideForParents)) {
    return HIDDEN;
  }
  return DISPLAY;
};

const computeSecretConnectionNoticeVisibility = (
  originalSettings: DmpConnectorSettings | undefined,
  settings: DmpConnectorSettings,
) => {
  if (!originalSettings?.secretConnection) return false;

  return !settings.secretConnection;
};

const computeParentsVisibilityNoticeVisibility = (
  originalSettings: DmpConnectorSettings | undefined,
  settings: DmpConnectorSettings,
) => {
  if (!originalSettings?.securityLabels?.includes(SecurityLabelEnum.HideForParents)) return false;

  return !settings?.securityLabels?.includes(SecurityLabelEnum.HideForParents);
};

const isSecretConnectionDisabled = (
  originalSettings: DmpConnectorSettings | undefined,
  isReadOnly: boolean,
) => {
  if (isReadOnly) return true;

  if (!originalSettings) return false;

  return !originalSettings?.secretConnection;
};

const isHideForParentsDisabled = (
  originalSettings: DmpConnectorSettings | undefined,
  isReadOnly: boolean,
) => {
  if (isReadOnly) return true;

  if (!originalSettings) return false;

  return !originalSettings?.securityLabels?.includes(SecurityLabelEnum.HideForParents);
};

export const SecretConnectionBlock = ({
  originalSettings,
  settings,
  setSettings,
  isReadOnly,
}: SecretConnectionBlockProps) => {
  const { t } = useTranslation();

  const switchSecretConnection = (
    checked: boolean,
    _event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (isReadOnly) return;

    setSettings((s: DmpConnectorSettings) => {
      const securityLabels: Set<SecurityLabelEnum> = new Set(s.securityLabels);
      securityLabels.add(SecurityLabelEnum.HideForParents);
      tracking.trackSecretConnectionChanged(checked);
      return {
        ...s,
        secretConnection: checked,
        securityLabels: Array.from(securityLabels),
      };
    });
  };

  const handleParentsVisibilityChange = (event: RadioChangeEvent) => {
    if (isReadOnly) return;

    if (event.target.value === DISPLAY) {
      setSettings((s) => {
        const securityLabels = s.securityLabels?.filter(
          (l) => l !== SecurityLabelEnum.HideForParents,
        );
        tracking.trackConnectorSecurityLabelChanged(securityLabels ?? []);
        return {
          ...s,
          securityLabels,
        };
      });
      return;
    }

    setSettings((s) => {
      const securityLabels = Array.from(
        new Set([...(s.securityLabels || []), SecurityLabelEnum.HideForParents]),
      );
      tracking.trackConnectorSecurityLabelChanged(securityLabels);
      return {
        ...s,
        securityLabels,
      };
    });
  };

  const secretConnectionSwitchLabel = settings.secretConnection
    ? t('documents.integrations.dmp.secretConnection.enabled')
    : t('documents.integrations.dmp.secretConnection.disabled');

  const displayParentsVisibilityNotice = computeParentsVisibilityNoticeVisibility(
    originalSettings,
    settings,
  );
  const displaySecretConnectionNotice = computeSecretConnectionNoticeVisibility(
    originalSettings,
    settings,
  );

  const secretConnectionDisabled = isSecretConnectionDisabled(originalSettings, isReadOnly);
  const hideForParentsDisabled = isHideForParentsDisabled(originalSettings, isReadOnly);

  return (
    <div className={styles.wrapper}>
      <p className={styles.titleWrapper}>
        <Icon name="Incognito" noContainer size={20} color={colors.grey[8]} />
        <Typography.Title className={styles.title} level={5}>
          {t('documents.integrations.dmp.secretConnection.title')}
        </Typography.Title>
      </p>
      <p>
        <Tooltip
          className={styles.secretConnectionWrapper}
          placement="top-start"
          content={
            secretConnectionDisabled
              ? t('documents.integrations.dmp.secretConnection.secretConnectionTooltip')
              : ''
          }
        >
          <Switch
            checked={settings.secretConnection}
            onChange={switchSecretConnection}
            size="small"
            disabled={secretConnectionDisabled}
          />
          <Typography.Text className={styles.secretConnectionText}>
            {secretConnectionSwitchLabel}
          </Typography.Text>
        </Tooltip>
      </p>
      {displaySecretConnectionNotice && (
        <BannerCard
          className={styles.secretConnectionNoticeWrapper}
          type="warning"
          iconName="RoadSignWarning"
          title={t('documents.integrations.dmp.notices.secretConnection.title')}
          message={t('documents.integrations.dmp.notices.secretConnection.message')}
        />
      )}
      <p>
        <Typography.Text>
          {t('documents.integrations.dmp.secretConnection.description')}
        </Typography.Text>
      </p>
      <Form.Item
        className={styles.parentsVisibilityForm}
        htmlFor="parentsVisibility"
        label={
          <Typography.Text strong>
            {t('documents.integrations.dmp.secretConnection.parentsVisibilityTitle')}
          </Typography.Text>
        }
      >
        <Radio.Group
          id="visibility"
          value={computeParentsVisibility(settings?.securityLabels || [])}
          onChange={handleParentsVisibilityChange}
          disabled={isReadOnly}
        >
          <Space direction="vertical">
            <Tooltip
              content={
                settings.secretConnection
                  ? t('documents.integrations.dmp.secretConnection.displayForParentsTooltip')
                  : ''
              }
            >
              <span className="inline-block">
                <Radio
                  value={DISPLAY}
                  key={DISPLAY}
                  aria-label={DISPLAY}
                  disabled={settings.secretConnection || isReadOnly}
                >
                  <Trans i18nKey="documents.integrations.dmp.visibilityLabels.DISPLAY_FOR_PARENTS" />
                </Radio>
              </span>
            </Tooltip>
            <Tooltip
              content={
                hideForParentsDisabled
                  ? t('documents.integrations.dmp.secretConnection.hideForParentsTooltip')
                  : ''
              }
            >
              <span className="inline-block">
                <Radio
                  value={HIDDEN}
                  key={HIDDEN}
                  aria-label={HIDDEN}
                  disabled={hideForParentsDisabled}
                >
                  <Trans i18nKey="documents.integrations.dmp.visibilityLabels.HIDE_FOR_PARENTS" />
                </Radio>
              </span>
            </Tooltip>
          </Space>
        </Radio.Group>
      </Form.Item>
      {displayParentsVisibilityNotice && (
        <BannerCard
          className={styles.parentsVisibilityNoticeWrapper}
          type="warning"
          iconName="RoadSignWarning"
          title={t('documents.integrations.dmp.notices.parentsVisibility.title')}
          message={t('documents.integrations.dmp.notices.parentsVisibility.message')}
        />
      )}
    </div>
  );
};
